import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  Search, Filter, RotateCcw, Download, Settings, User, ArrowLeft, Package
} from "lucide-react";

const todayIso = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};
const nDaysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

const SetupStrokeLedger = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    date_from: nDaysAgoIso(6),
    date_to: todayIso(),
    machine_code: "",
    reason_code: "",
    search: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    api.get("/machines").then((r) => setMachines(r.data || []));
    api.get("/machine-setup-strokes/reason-codes").then((r) => setReasons(r.data || []));
    fetchData(filters);
  }, []);

  const fetchData = (f) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (f.date_from) params.append("date_from", f.date_from);
    if (f.date_to) params.append("date_to", f.date_to);
    if (f.machine_code) params.append("machine_code", f.machine_code);
    if (f.reason_code) params.append("reason_code", f.reason_code);
    if (f.search) params.append("search", f.search);
    api.get(`/setup-stroke-sessions/report?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  const applyFilters = () => fetchData(filters);
  const resetFilters = () => {
    const fresh = {
      date_from: nDaysAgoIso(6), date_to: todayIso(),
      machine_code: "", reason_code: "", search: "",
    };
    setFilters(fresh);
    fetchData(fresh);
  };

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = ["Stroke Window Start", "Stroke Window End", "Machine", "Strokes", "Reason", "Remark", "Logged By"];
    const csv = [header.join(",")].concat(
      data.rows.map((r) => [
        r.started_at || r.logged_at,
        r.ended_at || r.logged_at,
        r.machine_code,
        r.strokes,
        r.reason_label,
        `"${(r.remark || "").replace(/"/g, '""')}"`,
        r.logged_by_name || r.logged_by,
      ].join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `setup-stroke-ledger-${filters.date_from}-to-${filters.date_to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <Settings size={22} className="text-amber-700" /> Setup Stroke Ledger
            </h1>
            <p className="text-sm text-slate-500">Every classified setup session with reason, remark and person.</p>
          </div>
          <button
            data-testid="ledger-export"
            onClick={exportCsv}
            disabled={!data?.rows?.length}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 px-3 py-2 rounded-lg"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <label className="text-xs font-bold text-slate-700">
              From
              <input
                data-testid="ledger-date-from"
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
            <label className="text-xs font-bold text-slate-700">
              To
              <input
                data-testid="ledger-date-to"
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
            <label className="text-xs font-bold text-slate-700">
              Machine
              <select
                data-testid="ledger-machine"
                value={filters.machine_code}
                onChange={(e) => setFilters({ ...filters, machine_code: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              >
                <option value="">All</option>
                {machines.map((m) => (
                  <option key={m.machine_code} value={m.machine_code}>
                    {m.machine_code}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-700">
              Reason
              <select
                data-testid="ledger-reason"
                value={filters.reason_code}
                onChange={(e) => setFilters({ ...filters, reason_code: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              >
                <option value="">All</option>
                {reasons.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-700">
              Search remark/person
              <div className="relative mt-1">
                <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
                <input
                  data-testid="ledger-search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                  placeholder="remark or user..."
                  className="w-full border-slate-300 rounded-lg text-sm pl-7 pr-2 py-2"
                />
              </div>
            </label>
            <div className="flex items-end gap-2">
              <button
                data-testid="ledger-apply"
                onClick={applyFilters}
                className="flex-1 inline-flex items-center justify-center gap-1 text-sm font-bold text-white bg-[#1A3560] hover:bg-[#152a4d] px-3 py-2 rounded-lg"
              >
                <Filter size={14} /> Apply
              </button>
              <button
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-1 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg"
                title="Reset filters"
              >
                <RotateCcw size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <SummaryCard label="Sessions" value={data.total_sessions} color="bg-slate-900" />
            <SummaryCard label="Setup Strokes" value={data.total_strokes} color="bg-amber-600" />
            <SummaryCard label="Reasons" value={Object.keys(data.by_reason || {}).length} color="bg-blue-600" />
            <SummaryCard label="Machines" value={Object.keys(data.by_machine || {}).length} color="bg-emerald-600" />
          </div>
        )}

        {/* Rows */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left">Stroke Window</th>
                  <th className="px-4 py-2 text-left">Machine</th>
                  <th className="px-4 py-2 text-right">Strokes</th>
                  <th className="px-4 py-2 text-left">Reason</th>
                  <th className="px-4 py-2 text-left">Remark</th>
                  <th className="px-4 py-2 text-left">Logged By</th>
                </tr>
              </thead>
              <tbody data-testid="ledger-tbody">
                {loading ? (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-8">Loading...</td></tr>
                ) : !data?.rows?.length ? (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-8">No sessions match your filters.</td></tr>
                ) : data.rows.map((r) => (
                  <tr key={r.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {r.started_at && r.ended_at ? (
                        <span title={`Real stroke window\nStart: ${new Date(r.started_at).toLocaleString("en-IN")}\nEnd: ${new Date(r.ended_at).toLocaleString("en-IN")}`}>
                          {new Date(r.started_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                          <span className="text-slate-300 mx-1">→</span>
                          {new Date(r.ended_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      ) : (
                        new Date(r.logged_at).toLocaleString("en-IN")
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 font-bold text-slate-900">
                        <Package size={12} className="text-slate-400" /> {r.machine_code}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-extrabold text-amber-700">{r.strokes}</td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                        {r.reason_label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={r.remark}>
                      {r.remark || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                        <User size={12} className="text-slate-400" /> {r.logged_by_name || r.logged_by}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`text-3xl font-extrabold ${color} bg-clip-text text-transparent`} style={{ WebkitBackgroundClip: "text" }}>{value}</p>
  </div>
);

export default SetupStrokeLedger;
